import React, { FC, useEffect, useRef, useState } from "react";
import {
	Grid,
	GridCellProps,
	GridColumn,
	GridColumnProps,
	GridCustomFilterCellProps,
	GridCustomHeaderCellProps,
	GridDetailRowProps,
	GridFilterChangeEvent,
	GridFilterOperators,
	GridRowProps,
	GridSortChangeEvent,
} from "@progress/kendo-react-grid";
import styles from "styles.module.css";
import { CustomRowRender } from "ui/grid/CustomGrid";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import {
	ExcelExport,
	ExcelExportProps,
} from "@progress/kendo-react-excel-export";
import {
	DetailExpandDescriptor,
	HeaderThElement,
	SortSettings,
} from "@progress/kendo-react-data-tools";

//@ts-ignore
import "./styles.css";
import { Skeleton } from "@progress/kendo-react-indicators";
import { lastColWithBtn } from "./lastColumnts";
import Info from "ui/Info";
import { OverLayLoading } from "ui/Loading";
import { useInfoStore } from "store/error";
import { CustomError, IconButtonActionType } from "interface";
import { PageAccessByMenu } from "interface/user";
import { checkActionButton } from "utils/helper";
import { handleFilteredItem } from "utils/statUtils";
import { process } from "@progress/kendo-data-query";
import {
	IntlProvider,
	load,
	LocalizationProvider,
	loadMessages,
} from "@progress/kendo-react-intl";
import i18n from "i18n";
import { messagesEn, messagesNo } from "./utilities/localization";
import { COLOR } from "utils/constant";
import CustomModal from "ui/modal/Modal";
import { useTranslation } from "react-i18next";

const unUsedHeight = 370; // manually adjusted height

loadMessages(messagesEn, "en");
loadMessages(messagesNo, "no");

export interface ColumnButtonProps {
	onclick?: (props: GridCellProps) => void;
	iconName: string;
	title?: string;
	showConditionValue?: any;
	conditionField?: string;
	alwaysShow?: boolean;
	style?: React.CSSProperties;
	disabled?: boolean;
	actionType?: IconButtonActionType;
}

interface ColumnButtonPropsFn extends ColumnButtonProps {
	handler?: (props: GridCellProps) => Omit<ColumnButtonProps, "iconName"> & {
		iconName?: string;
		title?: string;
	};
}

interface GridPageNoPagingProps {
	filter?: any;
	dataKey?: string;
	isExpandable?: boolean;
	hideScroll?: boolean;
	forceUpdate?: boolean;
	showLoader?: boolean;
	gridName?: string;

	exportGridAsPDFRef?: React.RefObject<GridPDFExport>;
	exportGridAsExcelRef?: React.RefObject<ExcelExport>;
	excelProps?: Partial<ExcelExportProps>;

	gridColumns: GridColumnProps[];
	itemAboveHeight?: number;
	lastColBtns?: ColumnButtonPropsFn[];
	lastColWidth?: string;
	lastColHeaderCell?: React.ComponentType<GridCustomHeaderCellProps>;
	showDetailIcon?: boolean;
	detailIconTitle?: string;
	pageAccess?: PageAccessByMenu | null;
	detailPage?: React.ComponentType<GridDetailRowProps>;
	apiMethod: (filter: any) => Promise<any>;
	deleteItem?: {
		api: (id: any) => Promise<any>;
		info: { title: string; description: string };
		showConditionValue?: any;
		conditionField?: string;
	};
	gridHeight?: number;
	gridMaxHeight?: number;
	handelShowRowBgColor?: (rowData: GridRowProps) => string;
	handelShowRowTextColor?: (rowData: GridRowProps) => string;
	getloadedData?: (data: any[]) => void;
	serverFilter?: boolean;
	filterable?: boolean;
	serverFilterMap?: {
		serverFieldName: string;
		filterFieldName: string;
	}[];
	filterOperators?: GridFilterOperators;
	sortable?: SortSettings;
	handleSortChange?: (e: GridSortChangeEvent) => void;
	serverSortValue?: Object;
}

const GridPageNoPaging: FC<GridPageNoPagingProps> = ({
	dataKey = "",
	filter = Object(),
	exportGridAsExcelRef,
	exportGridAsPDFRef,
	showDetailIcon = true,
	lastColWidth = "50px",
	isExpandable = true,
	hideScroll = false,
	gridHeight = null,
	showLoader = true,
	gridName = null,
	itemAboveHeight = 50,
	handelShowRowBgColor,
	handelShowRowTextColor = () => COLOR.Grey20,

	forceUpdate,
	serverFilter = false,
	serverFilterMap = [],
	filterable = false,
	...props
}) => {
	const [gridData, setGridData] = useState<any[]>([]);
	const requestInProgress = React.useRef(false);
	const deletingItemRef = React.useRef<any>();
	const [showDelete, setShowDelete] = useState(false);
	const [pageHeight, setPageHeight] = useState(window.innerHeight);
	const [loading, setLoading] = useState(false);
	const [detailExpand, setDetailExpand] = useState<
		DetailExpandDescriptor | undefined
	>();

	const overlayRef = useRef<HTMLDivElement>(null);
	const internalFilter = useRef<any>(null);

	const gridRef = useRef<HTMLDivElement>(null);

	const detailRef = useRef({ isOpen: false, index: 0, lastOpenKey: "" });

	useEffect(() => {
		const updateHeight = () => setPageHeight(window.innerHeight);
		window.addEventListener("resize", updateHeight);

		return () => window.removeEventListener("resize", updateHeight);
	}, []);

	const requestAllData = () => {
		if (!props.apiMethod) {
			return;
		}

		if (props.pageAccess && !props.pageAccess?.read) {
			return;
		}

		//allow the new request or queue it
		if (requestInProgress.current) return;

		requestInProgress.current = true;
		if (showLoader) {
			setLoading(true);
		}

		// Request all data without pagination
		props
			.apiMethod({
				...filter,
				skip: 0,
				take: 999999, // Large number to get all data
			})
			.then((response) => {
				if (response.data === null) {
					response.data = [];
				}

				if (props.getloadedData) {
					props.getloadedData(response.data);
				}

				setGridData([...response.data]);
				requestInProgress.current = false;
			})
			.catch((error) => {
				requestInProgress.current = false;
				if (error.response?.data) {
					if (error.response.data?.errors) {
						useInfoStore
							.getState()
							.setError(new CustomError(error.response?.data.title));
					} else {
						useInfoStore
							.getState()
							.setError(new CustomError(error.response?.data.returnText));
					}
				} else {
					useInfoStore.getState().setError(new CustomError(error.message));
				}
			})
			.finally(() => {
				requestInProgress.current = false;
				setLoading(false);
			});
	};

	useEffect(() => {
		requestAllData();
	}, [filter, forceUpdate]);

	useEffect(() => {
		if (internalFilter.current) {
			internalFilter.current = {
				...internalFilter.current,
				...(props.serverSortValue || {}),
			};
		} else {
			internalFilter.current = props.serverSortValue;
		}
		requestAllData();
	}, [JSON.stringify(props.serverSortValue || {})]);

	const handleLastColBtns = (): ColumnButtonPropsFn[] => {
		let lastColBtn = props.lastColBtns || [];
		if (props.lastColBtns && props.lastColBtns.length > 0 && props.pageAccess) {
			lastColBtn = props.lastColBtns.filter((btn) => {
				if (!btn.actionType) return true;
				return checkActionButton(btn.actionType, props.pageAccess);
			});
			props.lastColBtns = lastColBtn;
		}

		return lastColBtn;
	};

	const handleOpenDetail = (e: GridCellProps) => {
		if (!showDetailIcon) {
			return;
		}
		const key = e.dataItem[dataKey];
		if (!key) {
			return;
		}
		if (!detailRef.current.isOpen) {
			setDetailExpand({ [key]: true });
			detailRef.current = {
				isOpen: true,
				index: e.dataIndex,
				lastOpenKey: key,
			};
		} else {
			if (detailRef.current.lastOpenKey === key) {
				setDetailExpand({ [key]: false });
				detailRef.current = {
					isOpen: false,
					index: 0,
					lastOpenKey: "",
				};
			} else {
				setDetailExpand({ [key]: true });
				detailRef.current = {
					isOpen: true,
					index: e.dataIndex,
					lastOpenKey: key,
				};
			}
		}
	};

	const initDelete = (pros: GridCellProps) => {
		deletingItemRef.current = pros.dataItem;
		setShowDelete(true);
	};

	const handleDelete = () => {
		if (!deletingItemRef.current || !props.deleteItem) {
			return;
		}
		const id = deletingItemRef.current[dataKey];
		props.deleteItem
			.api(id)
			.then(() => {
				setGridData(gridData.filter((item) => item[dataKey] !== id));
				setShowDelete(false);
			})
			.catch((error: any) => {
				console.log(error);
			});
	};

	let deleteItem: any;

	if (props.deleteItem && props.pageAccess?.delete) {
		deleteItem = {
			btn: initDelete,
			showConditionValue: props.deleteItem.showConditionValue,
			conditionField: props.deleteItem.conditionField,
		};
	}

	const handleFilterChange = (e: GridFilterChangeEvent) => {
		handleFilterMap(e);
	};

	const handleFilterMap = (e: GridFilterChangeEvent) => {
		if (!e.filter) {
			internalFilter.current = {};
			requestAllData();
			return;
		}
		if (handleFilteredItem) {
			const filters = handleFilteredItem(e.filter);

			let mapObject: any = {};

			filters.forEach((filter) => {
				const map = serverFilterMap.find(
					(map) => map.filterFieldName === filter.field,
				);
				if (map) {
					mapObject[map.serverFieldName] = filter.values[0];
				}
			});

			internalFilter.current = mapObject;
			requestAllData();
		}
	};

	const otherClassName = props.detailPage
		? styles.tableColgroupColFirstChild
		: "";

	const currentHeight =
		gridHeight == null ? pageHeight - unUsedHeight - itemAboveHeight : "100%";

	const { t } = useTranslation();

	const resolvedDetailIconTitle = props.detailIconTitle ?? t("ShowDetails");

	return (
		<div style={{ height: currentHeight }}>
			<div ref={overlayRef}>
				<OverLayLoading show={loading} />
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				<ExcelExport
					data={gridData || []}
					ref={exportGridAsExcelRef}
					{...(props.excelProps || {})}
				>
					<Grid
						style={{
							maxHeight: currentHeight,
							paddingBottom: "30px",
							overflow: "auto",
						}}
						rowHeight={34}
						rows={{
							data: (row, props) =>
								CustomRowRender(
									row,
									handelShowRowBgColor,
									handelShowRowTextColor,
								),
						}}
						dataItemKey={dataKey}
						filterable={filterable}
						filterOperators={props.filterOperators}
						autoProcessData={true}
						// cells={{
						// 	filterCell: MyFilterCustomCell,
						// }}
						sortable={props.sortable}
						onFilterChange={handleFilterChange}
						//onSortChange={handleOnSortChange}
						data={gridData || []}
						detail={props.detailPage}
						detailExpand={detailExpand}
						className={
							otherClassName + ` ${hideScroll ? styles.HideScroll : ""}`
						}
					>
						{props.gridColumns.map((column, key) => (
							<GridColumn
								key={key}
								{...column}
								className={`${column.className}no-border`}
							/>
						))}
						<GridColumn
							filterable={false}
							headerCell={props.lastColHeaderCell}
							{...lastColWithBtn({
								width: lastColWidth,
								showDetails: showDetailIcon,
								openDetail: handleOpenDetail,
								items: handleLastColBtns(),
								showDelete: props.deleteItem ? true : false,
								deleteItem,
								detailIconTitle: resolvedDetailIconTitle,
							})}
						/>
					</Grid>
				</ExcelExport>
				{props.deleteItem && showDelete && (
					<CustomModal
						title={props.deleteItem.info.title}
						onClose={() => setShowDelete(false)}
						actions={[
							{
								label: t("Cancel"),
								onClick: () => setShowDelete(false),
								bgColor: COLOR.Green,
								color: "white",
							},
							{
								label: t("Yes"),
								onClick: handleDelete,
								bgColor: COLOR.StatusRed,
								color: "white",
							},
						]}
					>
						<p style={{ padding: "16px 0" }}>
							{props.deleteItem.info.description}
						</p>
					</CustomModal>
				)}
			</div>
			<GridPDFExport ref={exportGridAsPDFRef}>
				<Grid data={gridData} pageable={true}>
					{props.gridColumns.map((column, key) => (
						<GridColumn key={key} {...column} />
					))}
				</Grid>
			</GridPDFExport>
		</div>
	);
};

export default GridPageNoPaging;

const loadingCell = (tdElement: any, props?: any) => {
	const field = tdElement.field || "";
	if (tdElement.dataItem[field] === undefined) {
		return (
			<td>
				<Skeleton
					shape={"text"}
					style={{
						width: "100%",
					}}
				/>
			</td>
		);
	}
	return tdElement;
};
